import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, MapPin, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { PropertyCard, type PropertyCardData } from "@/components/public/PropertyCard";
import { AgentBrandBand } from "@/components/public/AgentBrandBand";
import { ServicesSection } from "@/components/public/ServicesSection";
import { InvestSection } from "@/components/public/InvestSection";
import { FaqSection } from "@/components/public/FaqSection";
import heroImage from "@/assets/hero.jpg";
import { useI18n } from "@/lib/i18n";
import { buildHead, PAGE_SEO, pickLang, IN_LANGUAGE, SITE_URL, localized } from "@/lib/seo";
import { faqJsonLd } from "@/lib/siteContent";
import { fetchPublishedArticles } from "@/lib/publicData";

export const Route = createFileRoute("/_public/")({
  head: ({ match }) => {
    const lang = pickLang(match.search);
    const copy = PAGE_SEO["home"]![lang];
    const { meta, links, url } = buildHead({ lang, path: "/", ...copy });
    return {
      meta,
      links: [
        ...links,
        { rel: "preload", as: "image", href: heroImage, fetchPriority: "high" },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Dahiana Marín Bienes Raíces",
            url: SITE_URL,
            inLanguage: IN_LANGUAGE[lang],
            description: copy.description,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(faqJsonLd(lang)),
        },
      ],
    };
  },
  component: HomePage,
});


function HomePage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [operation, setOperation] = useState<"venta" | "alquiler" | "alquiler_temporal">("venta");
  const [type, setType] = useState<string>("casas");
  const [location, setLocation] = useState<string>("");

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (location) {
      navigate({
        to: "/$operation/$type/$location",
        params: { operation, type, location },
      });
    } else {
      navigate({ to: "/$operation/$type", params: { operation, type } });
    }
  };

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <img
          src={heroImage}
          alt="Casa moderna en Paraguay al atardecer"
          width={1920}
          height={1280}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,oklch(0.18_0.04_215/0.55)_0%,oklch(0.15_0.04_215/0.85)_100%)]" />
        <div className="relative mx-auto max-w-7xl px-4 pt-20 pb-24 md:px-6 md:pt-32 md:pb-32">
          <div className="max-w-3xl">
            <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-accent backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" /> {t("hero.badge")}
            </p>
            <h1 className="font-serif text-5xl leading-[1.05] text-white md:text-7xl">
              {t("hero.title1")}
              <br />
              <span className="text-accent">{t("hero.title2")}</span>
            </h1>
            <p className="mt-6 max-w-xl text-base text-white/85 md:text-lg">
              {t("hero.subtitle")}
            </p>
          </div>

          {/* Buscador */}
          <form
            onSubmit={onSubmit}
            className="mt-10 rounded-2xl border border-white/20 bg-white/95 p-3 shadow-[0_20px_60px_-15px_rgb(0_0_0_/_0.4)] backdrop-blur md:mt-12 md:p-4"
          >
            <div className="mb-3 hidden gap-1 md:flex">
              {(["venta", "alquiler", "alquiler_temporal"] as const).map((op) => (
                <button
                  type="button"
                  key={op}
                  onClick={() => setOperation(op)}
                  className={`rounded-lg px-4 py-1.5 text-sm font-medium transition ${
                    operation === op
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-secondary"
                  }`}
                >
                  {op === "venta" ? t("search.buy") : op === "alquiler" ? t("search.rent") : t("search.temp")}
                </button>
              ))}
            </div>
            <div className="grid gap-2 md:grid-cols-[1fr_1.6fr_auto]">
              <select
                aria-label={t("search.operation")}
                value={operation}
                onChange={(e) => setOperation(e.target.value as typeof operation)}
                className="rounded-lg border border-input bg-background px-3 py-3 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-ring md:hidden"
              >
                <option value="venta">{t("search.venta")}</option>
                <option value="alquiler">{t("search.alquiler")}</option>
                <option value="alquiler_temporal">{t("search.alquiler_temporal")}</option>
              </select>

              <select
                aria-label={t("search.type")}
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="rounded-lg border border-input bg-background px-3 py-3 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="casas">{t("type.casas")}</option>
                <option value="departamentos">{t("type.departamentos")}</option>
                <option value="terrenos">{t("type.terrenos")}</option>
                <option value="oficinas">{t("type.oficinas")}</option>
                <option value="locales">{t("type.locales")}</option>
                <option value="duplex">{t("type.duplex")}</option>
              </select>


              <div className="relative md:col-span-1">
                <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder={t("search.placeholder")}
                  className="w-full rounded-lg border border-input bg-background pl-9 pr-3 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground shadow-soft transition hover:brightness-95"
              >
                <Search className="h-4 w-4" /> {t("search.submit")}
              </button>
            </div>
          </form>

          <div className="mt-6 flex flex-wrap gap-2 text-xs text-white/80">
            <span className="opacity-70">{t("search.popular")}</span>
            {[
              { label: t("popular.1"), to: "/venta/casas/villa-morra" },
              { label: t("popular.2"), to: "/venta/departamentos/recoleta" },
              { label: t("popular.3"), to: "/alquiler/casas/las-lomas" },
            ].map((q) => (
              <a
                key={q.to}
                href={q.to}
                className="rounded-full border border-white/30 px-3 py-1 hover:bg-white/10"
              >
                {q.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Marca personal */}
      <AgentBrandBand />

      {/* Featured properties */}
      <FeaturedProperties />

      {/* Servicios */}
      <ServicesSection />

      {/* Inversiones */}
      <InvestSection />

      {/* Últimos artículos del blog */}
      <LatestArticles />

      {/* Preguntas frecuentes */}
      <FaqSection />


      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6">
        <div className="rounded-2xl bg-secondary p-8 md:p-12">
          <div className="grid gap-6 md:grid-cols-[1.4fr_1fr] md:items-center">
            <div>
              <h2 className="text-2xl font-semibold text-foreground md:text-3xl">
                {t("home.sell.title")}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground md:text-base">
                {t("home.sell.sub")}
              </p>
            </div>
            <div className="md:justify-self-end">
              <a
                href="/vende-tu-propiedad"
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-soft transition hover:brightness-110"
              >
                {t("home.sell.cta")} <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function FeaturedProperties() {
  const { t } = useI18n();
  const { data, isLoading } = useQuery({
    queryKey: ["featured-properties"],
    queryFn: async () => {
      const { data } = await supabase
        .from("properties")
        .select("id, slug, title, title_en, title_pt, operation, property_type, price_amount, price_currency, address_text, bedrooms, bathrooms, garage_spots, area_m2, is_featured, is_opportunity, amenities")
        .neq("status", "borrador")
        .eq("is_featured", true)
        .order("created_at", { ascending: false })
        .limit(6);
      return (data ?? []) as PropertyCardData[];
    },
  });

  if (!isLoading && (!data || data.length === 0)) return null;

  return (
    <section className="mx-auto max-w-7xl px-4 py-16 md:px-6">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground md:text-3xl">{t("home.featured")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("home.featured.sub")}</p>
        </div>
      </div>
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-80 animate-pulse rounded-xl bg-secondary/60" />
            ))
          : data!.map((p) => <PropertyCard key={p.id} p={p} />)}
      </div>
    </section>
  );
}

function LatestArticles() {
  const { t, lang } = useI18n();
  const { data, isLoading } = useQuery({
    queryKey: ["latest-articles"],
    queryFn: fetchPublishedArticles,
  });

  const articles = data?.slice(0, 3) ?? [];

  if (!isLoading && articles.length === 0) return null;

  return (
    <section className="mx-auto max-w-7xl px-4 py-16 md:px-6">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground md:text-3xl">{t("home.blog")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("home.blog.sub")}</p>
        </div>
        <Link
          to="/blog"
          className="hidden items-center gap-1 text-sm font-medium text-primary hover:underline sm:inline-flex"
        >
          {t("home.blog.cta")} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-80 animate-pulse rounded-xl bg-secondary/60" />
            ))
          : articles.map((a) => (
              <Link
                key={a.id}
                to="/blog/$slug"
                params={{ slug: a.slug }}
                className="group overflow-hidden rounded-xl border border-border bg-card transition hover:shadow-card"
              >
                <div className="aspect-[16/10] overflow-hidden bg-secondary">
                  {a.cover_url ? (
                    <img
                      src={a.cover_url}
                      alt={localized(a, "title", lang) ?? ""}
                      width={1200}
                      height={750}
                      loading="lazy"
                      className="h-full w-full object-cover transition group-hover:scale-105"
                    />
                  ) : (
                    <div className="grid h-full w-full place-items-center font-serif text-4xl text-muted-foreground/40">
                      D
                    </div>
                  )}
                </div>
                <div className="flex h-[calc(100%-10rem)] flex-col p-5">
                  <h3 className="font-serif text-xl text-foreground">
                    {localized(a, "title", lang)}
                  </h3>
                  {localized(a, "excerpt", lang) && (
                    <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">
                      {localized(a, "excerpt", lang)}
                    </p>
                  )}
                  <div className="mt-auto inline-flex items-center gap-1 pt-4 text-sm font-medium text-primary">
                    {t("home.blog.cta")} <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </Link>
            ))}
      </div>

      <div className="mt-6 text-center sm:hidden">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
        >
          {t("home.blog.cta")} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </section>
  );
}
